import { useEffect, useState, useCallback } from "react";
const TODAY = new Date().toISOString().slice(0, 10);
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, Area } from "recharts";
import { Play, Sparkles, Loader2, Wand2, Save, GitCompare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import SimDiff from "@/components/SimDiff";
import Scenarios from "@/components/Scenarios";
import PeakEngine from "@/components/PeakEngine";
import { CalendarWorkspace } from '@/components/coaching/CalendarWorkspace';
import SquadOptimizer from "@/pages/SquadOptimizer";

const SCORE_LINES = [
  { key: "readiness", color: "#0a0a0a" },
  { key: "rest", color: "#6b7280" },
  { key: "sharpness", color: "#374151" },
  { key: "effort", color: "#9ca3af" },
];

function FinalCard({ label, cur, mod, invert }) {
  const delta = mod != null ? +(mod - cur).toFixed(1) : null;
  const good = delta == null ? null : invert ? delta < 0 : delta > 0;
  return (
    <div className="bg-background p-4" data-testid={`final-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="font-heading text-3xl font-black tabular-nums">{mod != null ? mod : cur}</span>
        {delta != null && delta !== 0 && (
          <span className={`text-sm font-bold tabular-nums ${good ? "text-traffic-green" : "text-traffic-red"}`}>
            {delta > 0 ? "+" : ""}{delta}
          </span>
        )}
      </div>
      {mod != null && <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">was {cur}</div>}
    </div>
  );
}

export default function Simulator({ athleteId: propId, embedded, squad: propSquad, academyId="", defaultScope, embeddedSquad }) {
  const [scope, setScope] = useState(defaultScope
    || (new URLSearchParams(window.location.search).get("squad") ? "squad" : "athlete"));
  const [rosterOpen, setRosterOpen] = useState(!embeddedSquad);
  const [teamAvg, setTeamAvg] = useState(null);
  const [squads, setSquads] = useState([]);
  const [squad, setSquad] = useState(propSquad || new URLSearchParams(window.location.search).get("squad") || "");
  const [athletes, setAthletes] = useState([]);
  const [athleteId, setAthleteId] = useState(propId || "");
  const [target, setTarget] = useState(new Date(Date.now() + 28 * 864e5).toISOString().slice(0, 10));
  const [dates, setDates] = useState([]);
  const [recovery, setRecovery] = useState([]);
  const [taper, setTaper] = useState(4);
  const [sim, setSim] = useState(null);
  const [running, setRunning] = useState(false);
  const [scenario, setScenario] = useState("current");
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planName, setPlanName] = useState("");
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");
  const [compare, setCompare] = useState(null);
  const [taskList, setTaskList] = useState([]);

  const loadPlans = useCallback(() => {
    if (athleteId) api.get(`/plans?athlete_id=${athleteId}`).then((r) => setPlans(r.data));
  }, [athleteId]);
  useEffect(() => { loadPlans(); }, [loadPlans]);

  useEffect(() => { api.get("/athletes").then((r) => { const rows=r.data.filter(a=>(!embeddedSquad || (a.teams || [a.sub_team || a.squad || a.sport]).includes(propSquad)) && (!academyId || (a.academy_id || '__unaffiliated__')===academyId));setAthletes(rows); if (!propId && rows[0]) setAthleteId(rows[0].athlete_id); }); }, [propId,propSquad,academyId,embeddedSquad]);
  useEffect(() => { if (scope === "squad") api.get("/squads").then((r) => setSquads(r.data.squads || [])).catch(() => setSquads([])); }, [scope]);
  useEffect(() => {
    if (scope !== "squad") return;
    setTeamAvg(null);
    api.get(`/squad/summary?${new URLSearchParams({squad,academy_id:academyId})}`)
      .then((r) => setTeamAvg(r.data)).catch(() => setTeamAvg(null));
  }, [scope, squad, academyId]);
  useEffect(() => { if (propSquad !== undefined) setSquad(propSquad || ""); }, [propSquad]);
  useEffect(() => {
    if (!athleteId) return;
    setSim(null); setInsight(null); setRecovery([]);
    const at = athletes.find((a) => a.athlete_id === athleteId);
    if (at?.target_date && at.target_date > TODAY) setTarget(at.target_date);
    api.get(`/tasks?athlete_id=${athleteId}`).then((r) => {
      const uniq = [...new Set(r.data.map((t) => t.date))].sort();
      setDates(uniq);
      setTaskList(r.data);
    });
  }, [athleteId, athletes]);

  const saveMatchDay = async () => {
    await api.post(`/athletes/${athleteId}/match-day?target_date=${target}`);
    toast.success("Match day set · simulator, optimizer & squad snap to it");
  };

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/simulate", { athlete_id: athleteId, target_date: target, start_date: TODAY, recovery_dates: recovery, taper_days: Number(taper) });
      setSim(r.data);
      setScenario(r.data.modified ? "modified" : "current");
    } catch (e) { toast.error("Simulation failed"); }
    setRunning(false);
  };

  const getInsight = async () => {
    setInsightLoading(true);
    try { const r = await api.get(`/athletes/${athleteId}/insight`); setInsight(r.data.insight); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI insight failed"); }
    setInsightLoading(false);
  };

  const toggleRec = (d) => setRecovery((p) => p.includes(d) ? p.filter((x) => x !== d) : [...p, d]);

  const savePlan = async () => {
    if (!planName.trim()) return toast.error("Name your plan");
    try {
      await api.post("/plans", { athlete_id: athleteId, name: planName, target_date: target, recovery_dates: recovery, taper_days: Number(taper) });
      toast.success("Plan saved"); setPlanName(""); loadPlans();
    } catch (e) { toast.error("Save failed"); }
  };
  const delPlan = async (id) => { await api.delete(`/plans/${id}`); loadPlans(); };
  const applyPlan = async (id, name) => {
    try { const r = await api.post(`/plans/${id}/apply`); toast.success(`Applied "${name}" · ${r.data.recovery_days} recovery, ${r.data.taper_days}d taper`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
  };
  // Day-types + templates in the current simulation window (start .. target)
  const planWindow = (() => {
    const start = TODAY;
    const inWin = taskList.filter((t) => t.date >= start && t.date <= target);
    const dtByDate = {};
    inWin.forEach((t) => { if (!dtByDate[t.date]) dtByDate[t.date] = t.day_type; });
    const dtCounts = {};
    Object.values(dtByDate).forEach((dt) => { dtCounts[dt] = (dtCounts[dt] || 0) + 1; });
    const templates = [...new Set(inWin.map((t) => t.from_template).filter(Boolean))];
    const sources = [...new Set(inWin.map((t) => t.source).filter(Boolean))];
    const windowDays = Math.max(1, Math.round((new Date(target) - new Date(start)) / 864e5));
    const planned = Object.keys(dtByDate).length;
    // Sparse = most of the run-in is empty, so there is barely any load for the simulator to move.
    const sparse = windowDays >= 3 && planned / windowDays < 0.4;
    return { dtCounts, templates, sources, days: planned, tasks: inWin.length, windowDays, sparse };
  })();
  const reloadTasks = () => {
    if (!athleteId) return;
    api.get(`/tasks?athlete_id=${athleteId}`).then((r) => {
      setDates([...new Set(r.data.map((t) => t.date))].sort());
      setTaskList(r.data);
    });
  };

  const runCompare = async () => {
    if (!cmpA || !cmpB || cmpA === cmpB) return toast.error("Pick two different plans");
    try { const r = await api.post("/plans/compare", { plan_id_a: cmpA, plan_id_b: cmpB }); setCompare(r.data); }
    catch (e) { toast.error("Compare failed"); }
  };
  const active = sim ? (scenario === "modified" && sim.modified ? sim.modified : sim.current) : null;
  const hasMod = sim?.modified;

  return (
    <div>
      <header className="min-h-16 border-b border-border flex flex-wrap items-center px-4 sm:px-8 py-3 gap-4 sticky top-0 bg-background z-10">
        <h1 className="font-heading text-xl font-black tracking-tight">Plan Simulator</h1>
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
          {scope === "squad" ? "Squad-wide scenario modelling" : "Project states to target date"}
        </span>
        <div className="ml-auto inline-flex border border-border overflow-hidden" data-testid="sim-scope">
          {[["athlete", "Athlete"], ["squad", "Squad"]].map(([id, label]) => (
            <button key={id} data-testid={`sim-scope-${id}`} onClick={() => setScope(id)}
              className={`px-3 py-1.5 text-xs transition-colors ${scope === id ? "bg-foreground text-background" : "hover:bg-surface"}`}>
              {label}
            </button>
          ))}
        </div>
      </header>

      {scope === "squad" ? (
        <div className="p-4 sm:p-6 space-y-6" data-testid="sim-squad">
          <div className="flex items-end gap-3 flex-wrap">
            {!embeddedSquad && (
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Squad</label>
                <select data-testid="sim-squad-select" value={squad} onChange={(e) => setSquad(e.target.value)}
                  className="inp mt-1.5 text-base py-2.5 min-w-[240px] font-medium">
                  <option value="">Population · all athletes</option>
                  {squads.map((sq) => <option key={sq.squad} value={sq.squad}>{`${sq.squad} · ${sq.athletes}`}</option>)}
                </select>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground max-w-md">
              One shared squad schedule, assessed together with joint attendance, individual forecasts and training rules. Review estimates before assignment.
            </p>
            {!embeddedSquad && (
              <div className="flex flex-wrap gap-2 ml-auto" data-testid="squad-tool-links">
                {[["budget", "Budget"], ["fixtures", "Fixtures"], ["film", "Film"], ["digest", "Digest"]].map(([t, label]) => (
                  <a key={t} href={`/?tab=${t}${squad ? `&squad=${encodeURIComponent(squad)}` : ""}`}
                    data-testid={`squad-tool-${t}`}
                    className="text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors">{label}</a>
                ))}
              </div>
            )}
          </div>

          <div className="border border-border" data-testid="sim-team-averages">
            <div className="px-4 py-2.5 border-b border-border font-heading font-bold tracking-tight text-sm">
              {teamAvg ? `${teamAvg.squad} · ${teamAvg.athletes} athletes` : "Loading team…"} · where the team is today
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-y sm:divide-y-0 divide-border">
              {[["avg_readiness", "Avg readiness"], ["avg_sharpness", "Avg sharpness"], ["avg_effort", "Avg effort"],
                ["avg_rest", "Avg rest"], ["avg_injury_risk", "Avg injury risk"], ["high_injury_risk", "High risk"],
                ["missing_checkin", "No check-in"]].map(([k, label]) => (
                <div key={k} className="p-3" data-testid={`team-avg-${k}`}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                  <div className="font-heading text-2xl font-black tabular-nums">
                    {teamAvg ? (teamAvg[k] ?? "—") : "…"}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {squad && academyId ? <CalendarWorkspace key={`${academyId}:${squad}`} squad={squad} academyId={academyId}/> : <p data-testid="squad-simulation-scope-required" className="text-sm">Open a specific academy team in Command Centre to simulate its whole authorized squad.</p>}
          <div className="border border-border" data-testid="sim-squad-roster">
            <button data-testid="sim-roster-toggle" onClick={() => setRosterOpen((o) => !o)}
              className="w-full px-4 py-2.5 border-b border-border font-heading font-bold tracking-tight text-sm flex items-center gap-2 hover:bg-surface transition-colors">
              <span className="flex-1 text-left">Roster & match days · set the day each athlete is peaking for</span>
              <span className="text-xs text-muted-foreground font-normal">{rosterOpen ? "Hide" : "Show"}</span>
            </button>
            {rosterOpen && <SquadOptimizer embedded squad={squad} academyId={academyId}/>}
          </div>
        </div>
      ) : (

      <div className="grid lg:grid-cols-[320px_minmax(0,1fr)]">
        {/* Controls */}
        <div className="border-r border-border p-6 space-y-5">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Athlete</label>
            <select data-testid="sim-athlete" value={athleteId} onChange={(e) => setAthleteId(e.target.value)} disabled={!!embedded} className="inp mt-1.5 disabled:opacity-60">
              {athletes.map((a) => <option key={a.athlete_id} value={a.athlete_id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Match Day (target)</label>
            <input type="date" data-testid="sim-target" value={target} onChange={(e) => setTarget(e.target.value)} className="inp mt-1.5" />
            <button data-testid="save-matchday" onClick={saveMatchDay} className="mt-2 text-xs w-full border border-border py-1.5 hover:bg-surface transition-colors">Set as match day (snap everywhere)</button>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Taper (days before target)</label>
            <input type="range" min="0" max="7" value={taper} onChange={(e) => setTaper(e.target.value)} className="w-full mt-2 accent-black" data-testid="sim-taper" />
            <div className="text-sm tabular-nums">{taper} day taper</div>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Convert to recovery days</label>
            <div className="mt-2 max-h-48 overflow-y-auto border border-border divide-y divide-border" data-testid="recovery-day-list">
              {dates.map((d) => (
                <label key={d} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface">
                  <input type="checkbox" checked={recovery.includes(d)} onChange={() => toggleRec(d)} className="accent-black" data-testid={`rec-${d}`} />
                  {fmtDate(d)}
                </label>
              ))}
            </div>
          </div>
          <button data-testid="run-sim" onClick={run} disabled={running} className="w-full bg-foreground text-background py-3 font-medium flex items-center justify-center gap-2 transition-colors hover:bg-neutral-800 disabled:opacity-50">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run Simulation
          </button>
          <p className="text-[11px] text-muted-foreground">
            Peak Readiness, Peak Sharpness, Best Balance and Personal Peak all live in the
            <b> Peak Engine</b> panel on the right · one engine, four intents.
          </p>
        </div>

        {/* Results */}
        <div className="p-6 min-w-0">
          <div className="border border-border mb-6" data-testid="plan-window">
            <div className="px-4 py-2.5 border-b border-border font-heading font-bold tracking-tight text-sm">Plan window · {planWindow.days} days to {fmtDate(target)} · {planWindow.tasks} tasks</div>
            <div className="p-3 flex flex-wrap gap-2 items-center text-xs">
              <span className="text-muted-foreground uppercase tracking-wider">Day types:</span>
              {Object.keys(planWindow.dtCounts).length === 0 ? <span className="text-muted-foreground">none set</span> :
                Object.entries(planWindow.dtCounts).map(([dt, n]) => <span key={dt} className="px-2 py-0.5 border border-border" data-testid={`window-dt-${dt}`}>{dt.replace("_", " ")} · {n}</span>)}
              <span className="text-muted-foreground uppercase tracking-wider ml-3">From:</span>
              {planWindow.templates.length === 0 && planWindow.sources.length === 0 ? <span className="text-muted-foreground">manual</span> :
                [...planWindow.templates, ...planWindow.sources].map((s) => <span key={s} className="px-2 py-0.5 border border-foreground" data-testid={`window-src-${s}`}>{s === "hyroutine" ? "Trifit routine" : s}</span>)}
            </div>
          </div>

          <div className="mb-6">
            <PeakEngine athleteId={athleteId} targetDate={target} onApplied={reloadTasks} />
          </div>

          {!sim ? (
            <div className="border border-border p-12 text-center text-sm text-muted-foreground" data-testid="sim-empty">
              Configure the plan and run the simulation to project effort, rest, sharpness, readiness,
              residual stress and injury risk to your target date. For the fastest route to this athlete's real
              ceiling or a match-day peak, use the <b>Peak Engine</b> above.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="inline-flex rounded-full border border-border overflow-hidden" data-testid="scenario-toggle">
                  {["current", "modified"].map((s) => (
                    <button key={s} data-testid={`scenario-${s}`} onClick={() => hasMod || s === "current" ? setScenario(s) : null}
                      disabled={s === "modified" && !hasMod}
                      className={`px-4 py-2 text-sm capitalize transition-colors ${scenario === s ? "bg-foreground text-background" : "hover:bg-surface"} disabled:opacity-40 disabled:cursor-not-allowed`}>
                      {s === "current" ? "Current Plan" : "Modified Plan"}
                    </button>
                  ))}
                </div>
                {!hasMod && (
                  <span className="text-xs text-muted-foreground" data-testid="no-modified-note">
                    No changes staged yet · pick recovery days, set a taper or run an optimiser to get a modified plan.
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border border border-border mb-6">
                <FinalCard label="Readiness" cur={sim.current.final.readiness} mod={hasMod ? sim.modified.final.readiness : null} />
                <FinalCard label="Sharpness" cur={sim.current.final.sharpness} mod={hasMod ? sim.modified.final.sharpness : null} />
                <FinalCard label="Effort" cur={sim.current.final.effort} mod={hasMod ? sim.modified.final.effort : null} />
                <FinalCard label="Rest" cur={sim.current.final.rest} mod={hasMod ? sim.modified.final.rest : null} />
                <FinalCard label="Residual Stress" cur={sim.current.final.residual_stress} mod={hasMod ? sim.modified.final.residual_stress : null} invert />
                <FinalCard label="Injury Risk" cur={sim.current.final.injury_risk} mod={hasMod ? sim.modified.final.injury_risk : null} invert />
              </div>

              <div className="border border-border p-4" data-testid="sim-chart">
                <div className="flex items-center gap-4 mb-3 flex-wrap">
                  {SCORE_LINES.map((l) => (
                    <span key={l.key} className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
                      <span className="w-4 h-0.5" style={{ background: l.color }} /> {l.key}
                    </span>
                  ))}
                  <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-traffic-red">
                    <span className="w-4 h-0.5 bg-traffic-red" /> injury risk
                  </span>
                  <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
                    <span className="w-4 h-0.5" style={{ background: "#7C3AED" }} /> residual stress
                  </span>
                  {sim.data_confidence != null && (
                    <span className="ml-auto text-xs px-2 py-1 border border-border tabular-nums" data-testid="sim-confidence">
                      Model confidence <b className="text-foreground">{sim.data_confidence}%</b>
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={active.trajectory.map((p) => ({
                    ...p,
                    readiness_band: [p.readiness_lo, p.readiness_hi],
                    rest_band: [p.rest_lo, p.rest_hi],
                    sharpness_band: [p.sharpness_lo, p.sharpness_hi],
                    effort_band: [p.effort_lo, p.effort_hi],
                    injury_band: [p.injury_risk_lo, p.injury_risk_hi],
                  }))} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="#e5e5e5" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => new Date(d).getDate()} stroke="#9ca3af" />
                    <YAxis yAxisId="score" domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <Tooltip contentStyle={{ border: "1px solid #0a0a0a", borderRadius: 0, fontSize: 12 }} />
                    {SCORE_LINES.map((l) => (
                      <Area yAxisId="score" key={`band-${l.key}`} type="monotone" dataKey={`${l.key}_band`} stroke="none" fill={l.color} fillOpacity={0.1} isAnimationActive={false} activeDot={false} legendType="none" />
                    ))}
                    <Area yAxisId="score" type="monotone" dataKey="injury_band" stroke="none" fill="#DC2626" fillOpacity={0.08} isAnimationActive={false} activeDot={false} legendType="none" />
                    {SCORE_LINES.map((l) => <Line yAxisId="score" key={l.key} type="monotone" dataKey={l.key} stroke={l.color} dot={false} strokeWidth={2} />)}
                    <Line yAxisId="score" type="monotone" dataKey="injury_risk" stroke="#DC2626" dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
                    <Line yAxisId="score" type="monotone" dataKey="residual_stress" stroke="#7C3AED" dot={false} strokeWidth={1.5} strokeDasharray="2 3" />
                  </ComposedChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="sim-band-note">
                  Shaded bands are the model's ~80% forecast interval · they widen further out and when data is sparse. This is model uncertainty, <b>not</b> a clinical prediction.
                </p>
              </div>

              <SimDiff sim={sim} recovery={recovery} taper={Number(taper)} target={target} />

              <Scenarios athleteId={athleteId} sim={sim} recovery={recovery} taper={taper} target={target} />

              <div className="border border-border mt-6" data-testid="ai-insight-panel">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <span className="font-heading font-bold tracking-tight flex items-center gap-2"><Sparkles className="w-4 h-4" /> Trifit AI · Risk Read-out</span>
                  <button data-testid="get-insight" onClick={getInsight} disabled={insightLoading} className="text-xs px-3 py-1.5 bg-foreground text-background flex items-center gap-2 transition-colors hover:bg-neutral-800 disabled:opacity-50">
                    {insightLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Ask Trifit AI
                  </button>
                </div>
                <div className="p-4 text-sm leading-relaxed">
                  {insight ? <p data-testid="insight-text">{insight}</p> : <p className="text-muted-foreground">Ask Trifit AI for this athlete's biggest performance risk over the next four weeks and what the coaching team should do.</p>}
                </div>
              </div>
            </>
          )}

          {/* Plan A/B Compare */}
          <div className="border border-border mt-6" data-testid="plans-panel">
            <div className="px-4 py-3 border-b border-border flex items-center gap-3 flex-wrap">
              <span className="font-heading font-bold tracking-tight flex items-center gap-2"><GitCompare className="w-4 h-4" /> Saved scenarios · A/B compare</span>
              <input data-testid="plan-name" value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="Name this scenario" className="inp text-sm ml-auto max-w-[220px]" />
              <button data-testid="save-plan" onClick={savePlan} disabled={!sim}
                className="text-sm px-3 py-1.5 rounded-lg bg-foreground text-background flex items-center gap-1.5 transition-colors hover:bg-neutral-800 disabled:opacity-40">
                <Save className="w-3 h-3" /> Save scenario
              </button>
            </div>
            <div className="p-4">
              {plans.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Saving keeps the <b className="text-foreground">scenario</b> ({recovery.length} recovery day(s), {taper}d taper) · your
                  real calendar is untouched until you press Apply. Save two to compare match-day projections.
                </p>
              ) : (
                <>
                  <div className="border border-border divide-y divide-border mb-4" data-testid="plan-list">
                    {plans.map((p) => (
                      <div key={p.plan_id} className="p-3 flex items-center gap-3 text-sm">
                        <span className="font-medium">{p.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5">till {fmtDate(p.target_date)}</span>
                        <span className="text-xs text-muted-foreground tabular-nums ml-auto">R{p.final.readiness} · Sh{p.final.sharpness} · Rest{p.final.rest} · Risk{p.final.injury_risk}%</span>
                        <button data-testid={`apply-plan-${p.plan_id}`} onClick={() => applyPlan(p.plan_id, p.name)} className="text-xs px-2.5 py-1 border border-foreground hover:bg-surface transition-colors">Apply</button>
                        <button data-testid={`del-plan-${p.plan_id}`} onClick={() => delPlan(p.plan_id)} className="text-muted-foreground hover:text-traffic-red transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <select data-testid="cmp-a" value={cmpA} onChange={(e) => setCmpA(e.target.value)} className="border border-border px-2 py-1.5 text-sm bg-background"><option value="">Plan A…</option>{plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}</select>
                    <span className="text-muted-foreground">vs</span>
                    <select data-testid="cmp-b" value={cmpB} onChange={(e) => setCmpB(e.target.value)} className="border border-border px-2 py-1.5 text-sm bg-background"><option value="">Plan B…</option>{plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}</select>
                    <button data-testid="run-compare" onClick={runCompare} className="text-sm px-3 py-1.5 border border-foreground hover:bg-surface transition-colors">Compare</button>
                  </div>
                  {compare && (
                    <div className="grid grid-cols-2 gap-px bg-border border border-border mt-4" data-testid="compare-result">
                      {["a", "b"].map((slot) => {
                        const c = compare[slot]; const f = c.sim.final;
                        return (
                          <div key={slot} className="bg-background p-4">
                            <div className="font-heading font-bold tracking-tight mb-3">{c.plan.name}</div>
                            {["readiness", "sharpness", "rest", "effort", "residual_stress", "injury_risk"].map((k) => (
                              <div key={k} className="flex justify-between text-sm border-b border-border py-1.5">
                                <span className="text-muted-foreground capitalize">{k.replace("_", " ")}</span>
                                <span className="font-heading font-bold tabular-nums">{f[k]}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
